import { Component, Input, OnInit } from '@angular/core';
import { FormBuilder, FormGroup, Validators, AbstractControl } from '@angular/forms';
import { MatSnackBar } from '@angular/material/snack-bar';
import { Client } from '../../models/client';
import { ClientService } from '../client.service';
import { ImageService } from '../../media/media.firebase.service';
import { AuthService } from '../../auth/auth.service';
import { UserService } from '../../auth/user.service';
import { User } from '../../models/user';
import { Router } from '@angular/router';
import { validateMediaFile } from '../../media/media-validation';
import { switchMap, of, tap, forkJoin } from 'rxjs';
import { calculateAge, minAgeValidator } from '../../shared/helpers';
import { Auth } from '@angular/fire/auth';

@Component({
  selector: 'app-client-detail-form',
  templateUrl: './client-detail-form.component.html',
  styleUrls: ['./client-detail-form.component.css']
})
export class ClientDetailFormComponent implements OnInit {

  loading = false;
  dataLoading = true;

  @Input() client: Client | null = null;
  @Input() userEmail: string | null = null;

  clientForm: FormGroup;
  selectedFile: File | null = null;
  selectedFileName = '';

  constructor(
    private fb: FormBuilder,
    private snackBar: MatSnackBar,
    private clientService: ClientService,
    private imageService: ImageService,
    private authService: AuthService,
    private userService: UserService,
    private router: Router,
    private auth: Auth
  ) {
    this.clientForm = this.fb.group({
      name: ['', Validators.required],
      date_of_birth: ['', [minAgeValidator(18)]],
      gender: [''],
      phone_number: [''],
      email: ['', [Validators.required, Validators.email]],
      profile_image_url: [''],
    });
  }

  ngOnInit(): void {
    const userId = this.authService.getCurrentUid();
    const firebaseUser = this.auth.currentUser;

    if (!userId) {
      this.dataLoading = false;
      return;
    }

    // Populate email from Firebase Auth (read-only)
    if (firebaseUser?.email) {
      this.clientForm.patchValue({ email: firebaseUser.email });
      this.clientForm.get('email')?.disable();
    }

    // Populate display name from Firebase Auth if available
    if (firebaseUser?.displayName) {
      this.clientForm.patchValue({ name: firebaseUser.displayName });
    }

    // Case 1: Parent passed a client object
    if (this.client) {
      this.patchClientForm(this.client, firebaseUser?.displayName);
      return;
    }

    // Case 2: Fetch client and user data from Firestore
    forkJoin({
      client: this.clientService.getClientByUserId(userId),
      user: this.userService.getUserById(userId)
    }).subscribe({
      next: ({ client, user }) => {
        // Patch with Firestore user data first (for display name fallback)
        if (user?.display_name && !firebaseUser?.displayName) {
          this.clientForm.patchValue({ name: user.display_name });
        }

        // Patch with client data if exists
        if (client) {
          this.client = client;
          this.patchClientForm(client, firebaseUser?.displayName);
        }

        this.dataLoading = false;
      },
      error: () => this.dataLoading = false
    });
  }


  /** Patch form with client data, preserving Firebase Auth values */
  private patchClientForm(client: Client, firebaseDisplayName?: string | null) {
    // Only patch date_of_birth if it's a valid date and not the epoch fallback (Date(0))
    const isValidDob = client.date_of_birth 
      && !isNaN(client.date_of_birth.getTime())
      && client.date_of_birth.getTime() > 0; // Exclude epoch fallback

    this.clientForm.patchValue({
      // Prefer Firebase Auth displayName, fall back to client name
      name: firebaseDisplayName || client.name || this.clientForm.get('name')?.value,
      date_of_birth: isValidDob ? client.date_of_birth : null,
      gender: client.gender || '',
      phone_number: client.phone_number,
      profile_image_url: client.profile_image_url
    });
    // Email is already set from Firebase Auth and disabled
    this.dataLoading = false;
  }


  // ---------------- FILE UPLOAD ---------------- //

  onFileSelected(event: Event): void {
    const input = event.target as HTMLInputElement;
    if (!input.files?.length) return;

    const file = input.files[0];
    const result = validateMediaFile(file, 'image');

    if (!result.valid) {
      this.snackBar.open(result.error || 'Invalid file.', 'Close', { duration: 4000 });
      return;
    }

    this.selectedFile = file;
    this.selectedFileName = file.name;

    const reader = new FileReader();
    reader.onload = () => {
      this.clientForm.patchValue({ profile_image_url: reader.result });
    };
    reader.readAsDataURL(file);
  }


  triggerFileInput(): void {
    document.getElementById('profile-image')?.click();
  }


  // ---------------- SUBMIT LOGIC ---------------- //

  async onSubmit(): Promise<void> {
    if (!this.clientForm.valid) return;

    const userId = this.authService.getCurrentUid();
    if (!userId) return;

    this.loading = true;

    const raw = this.clientForm.getRawValue();

    // Handle optional date of birth
    let dob: Date | null = null;
    let age = '';
    const rawDob = raw.date_of_birth;
    if (rawDob) {
      if (rawDob instanceof Date) {
        dob = rawDob;
      } else {
        dob = new Date(rawDob);
      }
      // Only validate if a date was provided
      if (isNaN(dob.getTime())) {
        this.snackBar.open('Invalid date of birth. Please enter a valid date.', 'Close', { duration: 3000 });
        this.loading = false;
        return;
      }
      age = calculateAge(dob).toString();
    }

    let clientData = new Client({
      client_id: this.client?.client_id || '',
      user_id: userId,
      name: raw.name,
      date_of_birth: dob || new Date(0), // Use epoch as fallback for Client model
      age: age,
      gender: raw.gender || '',
      phone_number: raw.phone_number,
      email: raw.email,
      profile_image_url: raw.profile_image_url
    });

    // Upload image if new file selected
    if (this.selectedFile) {
      try {
        const url = await this.imageService.uploadImage(
          this.selectedFile,
          `profile_images/${Date.now()}_${this.selectedFile.name}`
        );
        clientData.profile_image_url = url;
      } catch {
        this.snackBar.open('Image upload failed', 'Close', { duration: 3000 });
        this.loading = false;
        return;
      }
    }

    const save$ = this.client
      ? this.clientService.updateClient(clientData)
      : this.clientService.createClient(clientData);

    save$
      .pipe(
        switchMap(() => this.userService.getUserById(userId)),
        switchMap((user) => {
          if (!user) return of(null);
          const updatedUser = new User({ ...user, has_profile: true, is_model: false });
          return this.userService.updateUser(updatedUser);
        })
      )
      .subscribe({
        next: () => {
          this.snackBar.open(
            this.client ? 'Client updated successfully' : 'Client created successfully',
            'Close',
            { duration: 3000 }
          );

          if (!this.client) {
            this.router.navigate(['/models']);
          }

          this.loading = false;
        },
        error: () => {
          this.snackBar.open('Failed to save client', 'Close', { duration: 3000 });
          this.loading = false;
        }
      });
  }


  onClose() {
    this.router.navigate(['../']);
  }

}


// minAgeValidator now imported from shared/helpers
